import React from 'react';
import { ContractProgress } from '../../types/contract';
import { CheckCircle, Circle, PlayCircle, XCircle } from 'lucide-react';

interface ProgressMetricsProps {
  progress: ContractProgress;
  currency?: string;
}

export const ProgressMetrics: React.FC<ProgressMetricsProps> = ({
  progress,
  currency = 'CNY',
}) => {
  const metrics = [
    {
      label: '已完成',
      value: progress.completedMilestones,
      icon: CheckCircle,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
    },
    {
      label: '进行中',
      value: progress.inProgressMilestones,
      icon: PlayCircle,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
    },
    {
      label: '未开始',
      value: progress.notStartedMilestones,
      icon: Circle,
      color: 'text-slate-600',
      bgColor: 'bg-slate-50',
    },
    {
      label: '已取消',
      value: progress.cancelledMilestones,
      icon: XCircle,
      color: 'text-red-600',
      bgColor: 'bg-red-50',
    },
  ];

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6">
      <h3 className="text-lg font-semibold text-slate-900 mb-6">进度概览</h3>

      {/* Progress Bar */}
      <div className="mb-6">
        <div className="flex items-center justify-between text-sm text-slate-600 mb-2">
          <span>整体完成度</span>
          <span className="font-semibold text-lg text-slate-900">
            {progress.progressPercentage}%
          </span>
        </div>
        <div className="w-full h-3 bg-slate-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-blue-500 to-green-500 rounded-full transition-all duration-500"
            style={{ width: `${progress.progressPercentage}%` }}
          />
        </div>
        <div className="flex items-center justify-between text-xs text-slate-500 mt-2">
          <span>
            {progress.completedMilestones} / {progress.totalMilestones} 里程碑
          </span>
        </div>
      </div>

      {/* Milestone Status Grid */}
      <div className="grid grid-cols-2 gap-3 mb-6">
        {metrics.map((metric, index) => (
          <div
            key={index}
            className={`${metric.bgColor} rounded-xl p-4 flex items-center gap-3`}
          >
            <metric.icon className={`w-5 h-5 ${metric.color} flex-shrink-0`} />
            <div>
              <p className="text-2xl font-bold text-slate-900">{metric.value}</p>
              <p className="text-xs text-slate-600">{metric.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Payment Progress */}
      <div className="pt-6 border-t border-slate-200">
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-600">已支付</span>
            <span className="text-sm font-semibold text-green-600">
              {currency === 'CNY' ? '¥' : '$'}
              {progress.totalPaid.toLocaleString()}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-600">待支付</span>
            <span className="text-sm font-semibold text-slate-900">
              {currency === 'CNY' ? '¥' : '$'}
              {progress.totalPending.toLocaleString()}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
